/**
 * Telegram cards for the big moments (city captured/lost, Kessel, intercepts, capitulation,
 * research): a paper slip slides in, the rubber stamp thumps down, and it files itself away.
 */
import { dayOf } from '@friction/engine';
import { useEffect, useState } from 'react';
import { bus } from '../../game/bus';
import { formatClock } from '../../game/format';
import { useT } from '../../i18n';
import { cx } from '../common/controls';
import { Paper, Stamp } from '../common/Panel';
import hud from './hud.module.css';
import { hudStore, type Telegram, useHud } from './hudStore';

const LIFETIME_MS = 9000;

/**
 * Telegraphese: sentences end in STOP. A full stop after a digit is a German ordinal
 * ("11. Infanteriedivision"), not the end of a sentence.
 */
const stopify = (text: string) => text.replace(/(?<!\d)\.(\s+|$)/g, ' STOP ').trim();

function Card({ t: card, index }: { t: Telegram; index: number }) {
  const t = useT();
  const text = card.text[t.locale];
  const [leaving, setLeaving] = useState(false);
  const [hover, setHover] = useState(false);
  useEffect(() => {
    if (hover) return;
    const out = window.setTimeout(() => setLeaving(true), LIFETIME_MS);
    return () => clearTimeout(out);
  }, [hover]);
  useEffect(() => {
    if (!leaving) return;
    const done = window.setTimeout(() => hudStore.getState().dismissTelegram(card.id), 200);
    return () => clearTimeout(done);
  }, [leaving, card.id]);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={() => {
        if (card.hex >= 0) bus.emit('focusHex', { hex: card.hex });
        setLeaving(true);
      }}
      title={card.hex >= 0 ? t('ui.showOnMap') : t('ui.dismiss')}
    >
      <Paper
        className={cx(hud.telegram, leaving && hud.telegramOut, card.tone === 'bad' && hud.tgBad)}
        style={{ ['--tilt' as string]: `${index % 2 ? 0.7 : -0.9}deg` }}
      >
        <div className={hud.tgHead}>
          <span>{t('tg.head')}</span>
          <span>{t('time.stamp', { day: dayOf(card.tick), clock: formatClock(card.tick) })}</span>
        </div>
        <div className={hud.tgTitle}>{text.title}</div>
        <div className={hud.tgBody}>{stopify(text.body)}</div>
        <Stamp
          className={hud.tgStamp}
          tone={card.tone === 'good' ? 'green' : card.tone === 'neutral' ? 'blue' : 'red'}
          rotate={-12 + (index % 3) * 4}
          animate
          delay={200}
        >
          {text.stamp}
        </Stamp>
      </Paper>
    </div>
  );
}

export function Telegrams() {
  const telegrams = useHud((h) => h.telegrams);
  if (!telegrams.length) return null;
  return (
    <div className={hud.telegrams}>
      {telegrams.map((t, i) => (
        <Card key={t.id} t={t} index={i} />
      ))}
    </div>
  );
}
